import { computed, reactive, toValue } from 'vue'
import { useRecordMeta } from './useRecordMeta'

/**
 * Creates a read-only dv-like object for preview contexts (data-loader views).
 * Composes useRecordMeta for metadata + wraps a pre-loaded record with no-op editing stubs.
 *
 * All params accept plain values or refs/computed for reactivity.
 *
 * @param {Object} options
 * @param {string|Ref<string>} options.appName
 * @param {string|Ref<string>} options.modelName
 * @param {string|Ref<string>} options.modelType
 * @param {Object|Ref<Object>} options.record - Pre-loaded record data
 */
export function useRecordPreview({ appName, modelName, modelType, record }) {
  const metaResult = useRecordMeta({ appName, modelName, modelType })
  const resolvedRecord = computed(() => toValue(record))

  return reactive({
    // meta (from useRecordMeta)
    meta: metaResult.meta,
    isMetaReady: metaResult.isMetaReady,
    modelAttributes: metaResult.modelAttributes,
    viewAttributes: metaResult.viewAttributes,
    customProperties: metaResult.customProperties,

    // identity
    appName: computed(() => toValue(appName)),
    modelName: computed(() => toValue(modelName)),
    modelType: computed(() => toValue(modelType)),

    // record (pre-loaded, read-only)
    record: resolvedRecord,
    editedRecord: resolvedRecord,

    // hide sections irrelevant in previews
    hideSharing: true,
    hideOwnershipLifecycle: true,

    // editing stubs (no-ops)
    isEditing: false,
    edit: () => {},
    resetField: () => {},
    resetFields: () => {},
  })
}
